/*
  component-mega-menu.js
  ---------------------------------------------------------------------------
  Drives the desktop mega menu. Panels are rendered as siblings of <header>
  (see header.liquid), not nested inside a .mega-menu-item, so they can't be
  clipped/trapped by a transformed or overflow:hidden header (used for
  section.settings.header_sticky_behavior's scroll/fixed modes). Because of
  that, hover-to-open can't be pure CSS (:hover only cascades to descendants)
  — this file owns show/hide entirely, plus keeping each panel's position in
  sync with the header's current bounding box.

  Trigger <-> panel pairing is done via a shared data-mega-menu-id, since the
  two elements are no longer DOM ancestors of one another.

  Entirely self-contained — does not read from or write to DrawerMenu /
  component-drawer-menu.js, and is never loaded unless the mega menu feature
  is switched on (see sections/header.liquid + snippets/mega-menu-nav.liquid).
*/

class MegaMenu {
  constructor() {
    this.header = document.getElementById('MainHeader');
    this.items = document.querySelectorAll('.mega-menu-item');
    if (!this.header || !this.items.length) return;

    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.openPanel = null;
    this.openTrigger = null;

    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-mega-menu-trigger]');
      if (!trigger) return;
      const panel = this.getPanel(trigger);
      if (!panel) return;

      item.addEventListener('mouseenter', () => this.open(trigger, panel));
      item.addEventListener('mouseleave', () => this.maybeClose(item, panel));
      panel.addEventListener('mouseleave', () => this.maybeClose(item, panel));

      trigger.addEventListener('click', (event) => this.handleTriggerClick(event, trigger, panel));
      trigger.addEventListener('focus', () => this.open(trigger, panel));
    });

    document.addEventListener('click', this.handleOutsideClick.bind(this));
    document.addEventListener('keydown', this.handleKeydown.bind(this));
    window.addEventListener('resize', this.reposition.bind(this));
    window.addEventListener('scroll', this.reposition.bind(this), { passive: true });
  }

  getPanel(trigger) {
    const id = trigger.getAttribute('data-mega-menu-id');
    if (!id) return null;
    return document.querySelector('[data-mega-menu-panel][data-mega-menu-id="' + id + '"]');
  }

  open(trigger, panel) {
    if (!this.desktopQuery.matches) return;
    if (this.openPanel && this.openPanel !== panel) this.close();

    this.positionPanel(panel);
    panel.classList.add('is-visible');
    trigger.setAttribute('aria-expanded', 'true');
    this.openPanel = panel;
    this.openTrigger = trigger;
  }

  close() {
    if (!this.openPanel) return;
    this.openPanel.classList.remove('is-visible');
    if (this.openTrigger) this.openTrigger.setAttribute('aria-expanded', 'false');
    this.openPanel = null;
    this.openTrigger = null;
  }

  maybeClose(item, panel) {
    if (!this.desktopQuery.matches) return;
    // Small delay lets the pointer travel from the trigger down into the
    // panel (there's a visual gap: the panel is fixed-positioned, not a
    // direct sibling of the trigger) without the panel closing first.
    window.setTimeout(() => {
      // If the pointer has since moved straight to a different trigger,
      // open() will already have closed this panel and opened the new one
      // by the time this fires — this.openPanel now points at that new
      // panel, not this one. Bail out rather than closing whatever happens
      // to be open now; otherwise a fast move from one item to the next
      // flashes the new panel open for an instant and then this stale
      // timeout immediately closes it again.
      if (this.openPanel !== panel) return;
      if (item.matches(':hover') || panel.matches(':hover')) return;
      this.close();
    }, 60);
  }

  handleTriggerClick(event, trigger, panel) {
    if (!this.desktopQuery.matches) return; // below 1024px this button is hidden; nothing to do
    event.preventDefault();
    if (this.openPanel === panel) {
      this.close();
    } else {
      this.open(trigger, panel);
    }
  }

  handleOutsideClick(event) {
    if (!this.desktopQuery.matches || !this.openPanel) return;
    const withinTrigger = this.openTrigger && this.openTrigger.contains(event.target);
    const withinPanel = this.openPanel.contains(event.target);
    if (!withinTrigger && !withinPanel) this.close();
  }

  handleKeydown(event) {
    if ((event.key === 'Escape' || event.key === 'Esc') && this.openPanel) {
      const trigger = this.openTrigger;
      this.close();
      if (trigger) trigger.focus();
    }
  }

  positionPanel(panel) {
    const rect = this.header.getBoundingClientRect();
    const top = Math.max(rect.bottom, 0);
    panel.style.top = top + 'px';
    // Cap the panel to whatever viewport height remains below it, so a
    // column with a long list of links scrolls internally (via the
    // overflow-y: auto on .mega-menu-panel) instead of running off-screen
    // with no way to reach the items below the fold.
    panel.style.maxHeight = Math.max(window.innerHeight - top, 0) + 'px';
  }

  reposition() {
    if (this.openPanel) this.positionPanel(this.openPanel);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new MegaMenu());
} else {
  new MegaMenu();
}
